/**
 * ProphetsFooter — minimal single-row footer.
 *
 * Layout:
 *   LEFT   : © 2026 Prophets. All rights reserved.
 *   CENTER : X icon · Discord icon
 *   RIGHT  : Built on Solana.
 *
 * On mobile the row wraps into a centered stack (copyright top, socials
 * middle, Solana bottom). The previous large duplicated footer nav row
 * (brand lockup + Collection / Lore / Benefits / Games links + marketplace
 * bag icon) has been removed — the main navigation at the top of the
 * site already covers that ground.
 */

import { useLandingSettings, isUsableUrl } from "@/context/LandingSettingsContext";

const SOCIAL_ICONS = {
  x: (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className="w-4 h-4">
      <path d="M18.244 2H21l-6.535 7.47L22 22h-6.828l-4.77-6.24L4.8 22H2l7.02-8.02L2 2h6.914l4.32 5.71L18.244 2Zm-2.394 18h1.83L8.244 4H6.3l9.55 16Z" />
    </svg>
  ),
  discord: (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className="w-4 h-4">
      <path d="M20.317 4.369A19.79 19.79 0 0 0 16.558 3a13.29 13.29 0 0 0-.62 1.26 18.28 18.28 0 0 0-5.876 0A13.35 13.35 0 0 0 9.443 3a19.79 19.79 0 0 0-3.76 1.369C2.146 9.7 1.32 14.9 1.66 20.02a19.86 19.86 0 0 0 6.032 3.06c.487-.66.919-1.363 1.29-2.1a12.99 12.99 0 0 1-2.03-.98c.17-.126.335-.257.494-.39a14.15 14.15 0 0 0 12.11 0c.16.133.324.264.494.39-.647.383-1.327.716-2.03.98.37.737.803 1.44 1.29 2.1a19.83 19.83 0 0 0 6.032-3.06c.42-5.87-.72-11.02-3.024-15.65ZM9.03 16.66c-1.183 0-2.157-1.09-2.157-2.43 0-1.34.96-2.44 2.157-2.44 1.197 0 2.17 1.1 2.157 2.44 0 1.34-.96 2.43-2.157 2.43Zm5.94 0c-1.184 0-2.158-1.09-2.158-2.43 0-1.34.96-2.44 2.158-2.44 1.196 0 2.17 1.1 2.157 2.44 0 1.34-.96 2.43-2.157 2.43Z" />
    </svg>
  ),
};

export function ProphetsFooter() {
  const { settings } = useLandingSettings();
  const socials = [
    { key: "x",       label: "X",       href: settings.twitter_url },
    { key: "discord", label: "Discord", href: settings.discord_url },
  ];
  return (
    <footer
      data-testid="prophets-footer"
      className="prophets-footer relative w-full px-5 md:px-10 pt-8 pb-8"
    >
      <span aria-hidden="true" className="prophets-footer-topline" />

      <div className="relative mx-auto max-w-[1360px] w-full flex flex-col md:flex-row items-center md:items-center justify-between gap-4 md:gap-6 pt-6 border-t border-white/[0.06]">
        {/* LEFT — copyright */}
        <span
          data-testid="footer-copyright"
          className="p-mono text-[10px] tracking-[0.32em] uppercase text-white/45 order-2 md:order-1"
        >
          © 2026 Prophets. All rights reserved.
        </span>

        {/* CENTER — X · Discord */}
        <div
          data-testid="footer-socials"
          className="flex items-center gap-3 order-1 md:order-2"
        >
          {socials.map((s) => {
            const usable = isUsableUrl(s.href);
            return usable ? (
              <a
                key={s.key}
                href={s.href}
                target="_blank"
                rel="noreferrer"
                aria-label={s.label}
                data-testid={`footer-social-${s.key}`}
                className="prophets-footer-social"
              >
                {SOCIAL_ICONS[s.key]}
              </a>
            ) : (
              <button
                key={s.key}
                type="button"
                aria-label={s.label}
                aria-disabled="true"
                data-testid={`footer-social-${s.key}`}
                onClick={(e) => e.preventDefault()}
                className="prophets-footer-social"
                style={{ background: "transparent", padding: 0, font: "inherit" }}
              >
                {SOCIAL_ICONS[s.key]}
              </button>
            );
          })}
        </div>

        {/* RIGHT — Built on Solana */}
        <span
          data-testid="footer-solana"
          className="p-mono text-[10px] tracking-[0.32em] uppercase text-white/45 order-3"
        >
          Built on Solana.
        </span>
      </div>
    </footer>
  );
}
